import { Injectable } from '@angular/core';
import { NbTokenService } from '@nebular/auth';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay } from 'rxjs/operators';

/**
 * Short-lived cache for read-only requests that several components fire for the same data
 * (dashboard + report, per-row user lookups). Concurrent callers share one in-flight request
 * and the result is reused for `ttlMs`. Any GraphQL mutation clears it (see graphql.module.ts),
 * so data is fresh again right after a create/update/delete; a token change (login, logout,
 * refresh) clears it too, so another user never sees cached data.
 */
@Injectable({ providedIn: 'root' })
export class RequestCacheService {
  private entries = new Map<string, { at: number; ttlMs: number; result: Observable<any> }>();

  constructor(tokenService: NbTokenService) {
    tokenService.tokenChange().subscribe(() => this.clear());
  }

  get<T>(key: string, request: () => Observable<T>, ttlMs = 30_000): Observable<T> {
    const hit = this.entries.get(key);
    if (hit && Date.now() - hit.at < hit.ttlMs) return hit.result;
    const result = request().pipe(
      catchError(error => {
        this.entries.delete(key); // failures are never cached
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.entries.set(key, { at: Date.now(), ttlMs, result });
    return result;
  }

  clear(): void {
    this.entries.clear();
  }
}
